/*
 * The delete confirmation dialog.
 *
 * This is the component the requirement "present the user with a warning allowing
 * them to block the change, before it is performed" lands on. Its behaviour:
 *
 *   - Opening it asks the server what the delete would do. Nothing is written.
 *   - Every consequence is listed, with a count, ordered irreversible-first, and
 *     labelled with whether the database does it (cascade), we do it (cleanup /
 *     rewrite), or it cannot be done (blocked).
 *   - Cancel is the default action and the one keyboard focus lands on. Blocking the
 *     change is a single Escape.
 *   - When the delete destroys data irreversibly, the name of the thing has to be
 *     typed. That is deliberate friction, applied only where it is warranted -
 *     deleting an unused tag does not ask, deleting an article with history does.
 *   - Delete is only possible with the confirmation token the impact call issued, so
 *     the warning cannot be bypassed by any client, including this one.
 *   - If the impact changes between preview and confirmation the server answers 409
 *     and the dialog reloads the report instead of proceeding.
 *
 * Its words come from the chosen language's dictionary, `t.admin.articles` (S48).
 * What the server reports - the summary, each consequence, the choices - is shown
 * as it comes, and so is the name to type: it is compared with what is typed.
 */

import { useCallback, useMemo, useState } from 'react';
import type {
  DeleteImpact,
  DeleteResult,
  DeleteTargetKind,
  ImpactConsequence,
} from '@accotool-kb/shared';
import { hasDestructiveConsequence } from '@accotool-kb/shared';
import { useAdminApi } from '../../auth/AuthProvider.js';
import { useAction, useAsync } from '../../hooks/useAsync.js';
import { ErrorBanner, Loading, Modal } from '../../components/ui.js';
import { ApiError } from '../../api/client.js';
import { useMessages } from '../../i18n/language.js';
import { richText } from '../../i18n/richText.js';

export interface DeleteTarget {
  kind: DeleteTargetKind;
  /** GUID for an article or a release note, numeric id for a catalogue row. */
  targetId: string;
  /**
   * Shown in the dialog title before the server's own label arrives. For a
   * release note, `releaseNoteLabel(productName, version)` - the label the
   * server's impact report uses too, so the typed confirmation is the same
   * string at both ends.
   */
  label: string;
}

/*
 * What the title calls each kind, and what each effect is called, are the
 * dictionary's (`deleteDialog.title`, `deleteDialog.effect`): the wire value
 * 'release-note' is a token, not something to print (S42, 2026-10-12). Both are
 * Records over the shared unions there, so a kind or an effect added without a
 * label fails the build.
 */

export function DeleteImpactDialog({
  target,
  onClose,
  onDeleted,
}: {
  target: DeleteTarget | null;
  onClose: () => void;
  onDeleted: (result: DeleteResult) => void;
}): JSX.Element | null {
  const t = useMessages();
  const words = t.admin.articles.deleteDialog;
  const api = useAdminApi();
  const action = useAction();

  const [choices, setChoices] = useState<Record<string, string | null>>({});
  const [typedName, setTypedName] = useState('');

  const impactState = useAsync<DeleteImpact | null>(
    async () => {
      if (!target) return null;
      return api.deleteImpact({
        kind: target.kind,
        targetId: target.targetId,
        choices,
      });
    },
    [target?.kind, target?.targetId, JSON.stringify(choices)],
  );

  const impact = impactState.data;

  // Typing the name is required only where the delete destroys something that
  // cannot be recreated. Asking for it everywhere would train people to type it
  // without reading, which is worse than not asking.
  const requiresTypedName = useMemo(
    () => (impact ? hasDestructiveConsequence(impact) : false),
    [impact],
  );

  const expectedName = impact?.targetLabel ?? target?.label ?? '';
  const nameMatches = typedName.trim() === expectedName.trim();

  const close = useCallback(() => {
    setChoices({});
    setTypedName('');
    action.clearError();
    onClose();
  }, [action, onClose]);

  const confirm = useCallback(async () => {
    if (!target || !impact?.confirmation) return;

    const result = await action.run(() =>
      api.performDelete(target.kind, target.targetId, impact.confirmation!.token, { choices }),
    );

    if (result) {
      onDeleted(result);
      close();
    } else {
      // A 409 means the report is stale: fetch it again so the user sees what
      // changed rather than being told to try harder.
      impactState.reload();
    }
  }, [target, impact, action, api, choices, onDeleted, close, impactState]);

  if (!target) return null;

  const canConfirm =
    Boolean(impact?.canDelete && impact.confirmation) &&
    (!requiresTypedName || nameMatches) &&
    !action.running;

  return (
    <Modal
      open
      wide
      busy={action.running}
      onClose={close}
      title={words.title(target.kind, expectedName)}
      footer={
        <>
          <span className="faint" style={{ marginRight: 'auto' }}>
            {impact?.canDelete ? words.nothingChangedYet : words.cannotDelete}
          </span>
          <button type="button" className="button" onClick={close} disabled={action.running}>
            {t.admin.articles.shared.cancel}
          </button>
          <button
            type="button"
            className="button button-danger"
            onClick={() => void confirm()}
            disabled={!canConfirm}
          >
            {action.running ? words.deleting : words.deletePermanently}
          </button>
        </>
      }
    >
      {impactState.loading && !impact && <Loading label={words.working} />}

      {impactState.error && <ErrorBanner error={impactState.error} onRetry={impactState.reload} />}

      {action.error && (
        <ErrorBanner
          error={action.error}
          onDismiss={action.clearError}
          {...(action.error instanceof ApiError && action.error.isConcurrencyConflict
            ? { onRetry: impactState.reload }
            : {})}
        />
      )}

      {impact && (
        <>
          <p className="impact-summary">{impact.summary}</p>

          {impact.blockers.length > 0 && (
            <ul className="impact-list">
              {impact.blockers.map((blocker) => (
                <ConsequenceRow key={blocker.code} consequence={blocker} />
              ))}
            </ul>
          )}

          {impact.choices.map((choice) => (
            <div className="field" key={choice.name}>
              <label htmlFor={`choice-${choice.name}`}>
                {choice.label}
                {choice.required && <span aria-hidden="true"> *</span>}
              </label>
              <select
                id={`choice-${choice.name}`}
                value={choice.value ?? ''}
                onChange={(event) =>
                  setChoices((current) => ({
                    ...current,
                    [choice.name]: event.target.value || null,
                  }))
                }
              >
                <option value="">{words.choose}</option>
                {choice.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                    {option.detail ? ` — ${option.detail}` : ''}
                  </option>
                ))}
              </select>
              <p className="field-hint">{choice.help}</p>
            </div>
          ))}

          {impact.consequences.length > 0 && (
            <>
              <h3 style={{ fontSize: '0.95rem', marginTop: '1rem' }}>{words.whatWillHappen}</h3>
              <ul className="impact-list">
                {impact.consequences.map((consequence) => (
                  <ConsequenceRow key={consequence.code} consequence={consequence} />
                ))}
              </ul>
            </>
          )}

          {requiresTypedName && impact.canDelete && (
            <div className="confirm-phrase">
              <div className="field">
                <label htmlFor="confirm-name">
                  {richText(words.typeToConfirm, { name: <strong>{expectedName}</strong> })}
                </label>
                <input
                  id="confirm-name"
                  type="text"
                  value={typedName}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(event) => setTypedName(event.target.value)}
                  aria-describedby="confirm-name-hint"
                />
                <p className="field-hint" id="confirm-name-hint">
                  {words.typeToConfirmHint}
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

function ConsequenceRow({ consequence }: { consequence: ImpactConsequence }): JSX.Element {
  const words = useMessages().admin.articles.deleteDialog;
  return (
    <li className={`impact-item impact-item-${consequence.severity}`}>
      <h4>
        {consequence.title}
        <span className="impact-effect">{words.effect[consequence.effect]}</span>
      </h4>
      <p>{consequence.detail}</p>

      {consequence.items.length > 0 && (
        <details className="impact-details">
          <summary>
            {words.showItems(consequence.items.length, consequence.truncated ? consequence.count : null)}
          </summary>
          <ul className="impact-items">
            {consequence.items.map((item) => (
              <li key={item.id}>
                {item.href ? (
                  <a href={item.href} target="_blank" rel="noreferrer">
                    {item.label}
                  </a>
                ) : (
                  item.label
                )}
                {item.sublabel && <span className="faint"> — {item.sublabel}</span>}
              </li>
            ))}
          </ul>
          {consequence.truncated && (
            <p className="faint">
              {words.moreNotListed(consequence.count - consequence.items.length)}
            </p>
          )}
        </details>
      )}
    </li>
  );
}
